import React, { useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { Alert, Radio, RadioGroup, Select, SwitchHorizontal01, TextInput } from "@opendate-app/setlist";
import { toastSuccess } from "../../shared/toastHelper";
import DecisionModal from "../ClassesShared/DecisionModal";
import TintedAlert from "../ClassesShared/TintedAlert";
import { formatDate } from "../ClassesShared/classLabels";
import "../ClassesShared/classesShared.css";

const ATTENDEE_FIELDS = [
  { key: "first_name", label: "First name", message: "Enter the new attendee's first name." },
  { key: "last_name", label: "Last name", message: "Enter the new attendee's last name." },
  { key: "email", label: "Email", message: "Enter the new attendee's email." },
];
const fieldId = (key) => `transfer-attendee-${key}`;

const targetLabel = (target) => [
  target.name,
  target.note,
  formatDate(target.first_session_starts_at, target.time_zone) || "No sessions yet",
  `${target.spots_remaining} ${target.spots_remaining === 1 ? "spot" : "spots"} left`,
].filter(Boolean).join(" · ");

function TransferRegistrationModal({ registration, path, csrfToken, onClose, onTransferred }) {
  const openTargets = registration.transfer_targets;
  const [mode, setMode] = useState(openTargets.length > 0 ? "date" : "person");
  const [targetId, setTargetId] = useState(null);
  const [attendee, setAttendee] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const [working, setWorking] = useState(false);
  const [errors, setErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});

  const update = (field, value) => {
    setAttendee((current) => ({ ...current, [field]: value }));
    setFieldErrors((current) => ({ ...current, [field]: undefined }));
  };

  const validate = () => {
    if (mode === "date") {
      if (targetId) return true;
      setErrors(["Choose a new date to move this registration to."]);
      return false;
    }
    const missing = ATTENDEE_FIELDS.filter((field) => !attendee[field.key].trim());
    setFieldErrors(Object.fromEntries(missing.map((field) => [field.key, field.message])));
    if (missing.length === 0) return true;
    document.getElementById(fieldId(missing[0].key))?.focus();
    return false;
  };

  const submit = async () => {
    if (working || !validate()) return;
    setWorking(true);
    setErrors([]);
    const headers = { headers: { "X-CSRF-Token": csrfToken } };
    try {
      let resp;
      if (mode === "date") {
        resp = await axios.post(`${path}/transfer`, { target_class_instance_id: targetId }, headers);
      } else {
        resp = await axios.patch(path, { class_registration: attendee }, headers);
        toastSuccess(`Registration transferred to ${attendee.first_name.trim()} ${attendee.last_name.trim()}.`);
      }
      onTransferred(resp.data);
    } catch (e) {
      setErrors(e.response?.data?.errors?.base || ["Could not transfer the registration."]);
      setWorking(false);
    }
  };

  return (
    <DecisionModal
      type="brand"
      icon={SwitchHorizontal01}
      title="Transfer Registration"
      description={`Move ${registration.full_name}'s spot to another date of this class, or give it to someone else.`}
      confirmLabel={working ? "Transferring…" : "Transfer Registration"}
      onClose={onClose}
      onConfirm={submit}
      working={working}
    >
      {errors.length > 0 && (
        <div className="mb-3"><Alert color="error" role="alert" description={errors.join(" ")} /></div>
      )}
      <div className="mb-3">
        <div role="radiogroup" aria-label="Transfer type">
          <RadioGroup name="transfer-mode" value={mode} onChange={(value) => { setMode(value); setErrors([]); setFieldErrors({}); }}>
            <Radio value="date" label="Move to another date of this class" disabled={openTargets.length === 0} />
            <Radio value="person" label="Give this spot to another person" />
          </RadioGroup>
        </div>
        {openTargets.length === 0 && (
          <p className="classes-text-secondary small mb-0 mt-2" data-testid="transfer-date-unavailable">
            Moving to another date isn't available: no other upcoming date of this class at the same price has a spot open.
          </p>
        )}
      </div>
      {mode === "date" && openTargets.length > 0 && (
        <div>
          <Select
            label="New date"
            placeholder="Choose a date"
            options={openTargets.map((target) => ({ value: target.id, label: targetLabel(target) }))}
            value={targetId}
            hint={openTargets.find((target) => target.id === targetId)?.note}
            onChange={(value) => { setTargetId(value); setErrors([]); }}
          />
        </div>
      )}
      {mode === "person" && (
        <div>
          <div className="d-flex flex-wrap classes-gap-12">
            <div className="flex-grow-1"><TextInput id={fieldId("first_name")} label="First name" required value={attendee.first_name} error={fieldErrors.first_name} onChange={(e) => update("first_name", e.target.value)} /></div>
            <div className="flex-grow-1"><TextInput id={fieldId("last_name")} label="Last name" required value={attendee.last_name} error={fieldErrors.last_name} onChange={(e) => update("last_name", e.target.value)} /></div>
          </div>
          <div className="d-flex flex-wrap mt-2 classes-gap-12">
            <div className="flex-grow-1"><TextInput id={fieldId("email")} label="Email" required type="email" value={attendee.email} error={fieldErrors.email} onChange={(e) => update("email", e.target.value)} /></div>
            <div className="flex-grow-1"><TextInput label="Phone" value={attendee.phone} onChange={(e) => update("phone", e.target.value)} /></div>
          </div>
        </div>
      )}
      <div className="mt-3">
        <TintedAlert
          color="brand"
          title="No money moves"
          description={mode === "date"
            ? "No charge or refund is made and no email is sent. The attendee keeps their ticket and order."
            : registration.confirmation_resendable
              ? "No charge or refund is made. The new attendee gets a confirmation email with their own link. The old link stops working, and the original attendee is emailed that their spot was given away."
              : "No charge or refund is made. The old link stops working. Emails don't go out for this registration, so neither attendee is emailed."}
        />
      </div>
    </DecisionModal>
  );
}

TransferRegistrationModal.propTypes = {
  registration: PropTypes.shape({
    full_name: PropTypes.string,
    confirmation_resendable: PropTypes.bool,
    transfer_targets: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string,
      first_session_starts_at: PropTypes.string,
      time_zone: PropTypes.string,
      spots_remaining: PropTypes.number,
      note: PropTypes.string,
    })).isRequired,
  }).isRequired,
  path: PropTypes.string.isRequired,
  csrfToken: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onTransferred: PropTypes.func.isRequired,
};

export default TransferRegistrationModal;
